import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';
import type { RootState } from 'app/store';

type SourceType = 'upload' | 'sample' | 'dbt-cloud';
type DbtCloudImportStatusType = 'waiting-for-url' | 'repo-added' | 'import-completed';

export interface IDbtMigrationState {
  dbtExecutionMode: 'dataops_live' | 'snowflake';
  isSnowflakeAccountValidatedForDbt: boolean;
  dbtProjectFilename: string;
  seedJobSelected: boolean;
  testSourcesJobSelected: boolean;
  buildModelsJobSelected: boolean;
  testModelsJobSelected: boolean;
  documentationJobSelected: boolean;
  seedSelector: string;
  sourceSelector: string;
  modelSelector: string;
  dbtVersion: string;
  selectorTags: string[];
  selectorFiles: string[];
  source: SourceType;
  hasToUploadProject: boolean;
  isProjectNameFromUrl: boolean;
  dbtCloudImportStatus: DbtCloudImportStatusType;
  repositoryUrl: string;
  sshKeyId: number;
  publicKey: string;
  isPublicKeyCheckboxChecked: boolean;
}

export const initialState: IDbtMigrationState = {
  dbtExecutionMode: 'dataops_live',
  isSnowflakeAccountValidatedForDbt: false,
  dbtProjectFilename: '',
  seedJobSelected: true,
  testSourcesJobSelected: false,
  buildModelsJobSelected: true,
  testModelsJobSelected: true,
  documentationJobSelected: true,
  seedSelector: '*',
  sourceSelector: '*',
  modelSelector: '*',
  dbtVersion: '1.7',
  selectorTags: [],
  selectorFiles: [],
  source: 'upload',
  hasToUploadProject: true,
  isProjectNameFromUrl: false,
  dbtCloudImportStatus: 'waiting-for-url',
  repositoryUrl: '',
  sshKeyId: 0,
  publicKey: '',
  isPublicKeyCheckboxChecked: false,
};

export const dbtMigrationSlice = createSlice({
  name: 'dbtMigration',
  initialState,
  reducers: {
    setDbtExecutionMode: (state, action: PayloadAction<'dataops_live' | 'snowflake'>) => {
      state.dbtExecutionMode = action.payload;
    },
    setIsSnowflakeAccountValidatedForDbt: (state, action: PayloadAction<boolean>) => {
      state.isSnowflakeAccountValidatedForDbt = action.payload;
    },
    setDbtProjectFilename: (state, action: PayloadAction<string>) => {
      state.dbtProjectFilename = action.payload;
    },
    setSeedJobSelected: (state, action: PayloadAction<boolean>) => {
      state.seedJobSelected = action.payload;
    },
    setTestSourcesJobSelected: (state, action: PayloadAction<boolean>) => {
      state.testSourcesJobSelected = action.payload;
    },
    setBuildModelsJobSelected: (state, action: PayloadAction<boolean>) => {
      state.buildModelsJobSelected = action.payload;
    },
    setTestModelsJobSelected: (state, action: PayloadAction<boolean>) => {
      state.testModelsJobSelected = action.payload;
    },
    setDocumentationJobSelected: (state, action: PayloadAction<boolean>) => {
      state.documentationJobSelected = action.payload;
    },
    setSeedSelector: (state, action: PayloadAction<string>) => {
      state.seedSelector = action.payload;
    },
    setSourceSelector: (state, action: PayloadAction<string>) => {
      state.sourceSelector = action.payload;
    },
    setModelSelector: (state, action: PayloadAction<string>) => {
      state.modelSelector = action.payload;
    },
    setDbtVersion: (state, action: PayloadAction<string>) => {
      state.dbtVersion = action.payload;
    },
    setSelectorTags: (state, action: PayloadAction<string[]>) => {
      state.selectorTags = action.payload;
    },
    setSelectorFiles: (state, action: PayloadAction<string[]>) => {
      state.selectorFiles = action.payload;
    },
    setSource: (state, action: PayloadAction<SourceType>) => {
      state.source = action.payload;
    },
    setHasToUploadProject: (state, action: PayloadAction<boolean>) => {
      state.hasToUploadProject = action.payload;
    },
    setIsProjectNameFromUrl: (state, action: PayloadAction<boolean>) => {
      state.isProjectNameFromUrl = action.payload;
    },
    setDbtCloudImportStatus: (state, action: PayloadAction<DbtCloudImportStatusType>) => {
      state.dbtCloudImportStatus = action.payload;
    },
    setRepositoryUrl: (state, action: PayloadAction<string>) => {
      state.repositoryUrl = action.payload;
    },
    setSshKeyId: (state, action: PayloadAction<number>) => {
      state.sshKeyId = action.payload;
    },
    setPublicKey: (state, action: PayloadAction<string>) => {
      state.publicKey = action.payload;
    },
    setIsPublicKeyCheckboxChecked: (state, action: PayloadAction<boolean>) => {
      state.isPublicKeyCheckboxChecked = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => {
      return initialState;
    });
  },
});

export const {
  setDbtExecutionMode,
  setIsSnowflakeAccountValidatedForDbt,
  setDbtProjectFilename,
  setSeedJobSelected,
  setTestSourcesJobSelected,
  setBuildModelsJobSelected,
  setTestModelsJobSelected,
  setDocumentationJobSelected,
  setSeedSelector,
  setSourceSelector,
  setModelSelector,
  setDbtVersion,
  setSelectorTags,
  setSelectorFiles,
  setSource,
  setHasToUploadProject,
  setIsProjectNameFromUrl,
  setDbtCloudImportStatus,
  setRepositoryUrl,
  setSshKeyId,
  setPublicKey,
  setIsPublicKeyCheckboxChecked,
} = dbtMigrationSlice.actions;

export const selectDbtExecutionMode = (state: RootState): 'dataops_live' | 'snowflake' =>
  state.dbtMigration.dbtExecutionMode;
export const selectIsSnowflakeAccountValidatedForDbt = (state: RootState): boolean =>
  state.dbtMigration.isSnowflakeAccountValidatedForDbt;
export const selectDbtProjectFilename = (state: RootState): string => state.dbtMigration.dbtProjectFilename;
export const selectSeedJobSelected = (state: RootState): boolean => state.dbtMigration.seedJobSelected;
export const selectTestSourcesJobSelected = (state: RootState): boolean => state.dbtMigration.testSourcesJobSelected;
export const selectBuildModelsJobSelected = (state: RootState): boolean => state.dbtMigration.buildModelsJobSelected;
export const selectTestModelsJobSelected = (state: RootState): boolean => state.dbtMigration.testModelsJobSelected;
export const selectDocumentationJobSelected = (state: RootState): boolean =>
  state.dbtMigration.documentationJobSelected;
export const selectSeedSelector = (state: RootState): string => state.dbtMigration.seedSelector;
export const selectSourceSelector = (state: RootState): string => state.dbtMigration.sourceSelector;
export const selectModelSelector = (state: RootState): string => state.dbtMigration.modelSelector;
export const selectDbtVersion = (state: RootState): string => state.dbtMigration.dbtVersion;
export const selectSelectorTags = (state: RootState): string[] => state.dbtMigration.selectorTags;
export const selectSelectorFiles = (state: RootState): string[] => state.dbtMigration.selectorFiles;
export const selectSource = (state: RootState): SourceType => state.dbtMigration.source;
export const selectHasToUploadProject = (state: RootState): boolean => state.dbtMigration.hasToUploadProject;
export const selectIsProjectNameFromUrl = (state: RootState): boolean => state.dbtMigration.isProjectNameFromUrl;
export const selectDbtCloudImportStatus = (state: RootState): DbtCloudImportStatusType =>
  state.dbtMigration.dbtCloudImportStatus;
export const selectRepositoryUrl = (state: RootState): string => state.dbtMigration.repositoryUrl;
export const selectSshKeyId = (state: RootState): number => state.dbtMigration.sshKeyId;
export const selectPublicKey = (state: RootState): string => state.dbtMigration.publicKey;
export const selectIsPublicKeyCheckboxChecked = (state: RootState): boolean =>
  state.dbtMigration.isPublicKeyCheckboxChecked;

export default dbtMigrationSlice.reducer;
